import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Eye, Type, Contrast, Volume2 } from 'lucide-react';

const STORAGE_KEY = 'lerno_accessibility';

const getSettings = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch { return {}; }
};

const AccessibilityToggle = () => {
  const [settings, setSettings] = useState(getSettings);
  const [open, setOpen] = useState(false);

  const update = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    applySettings(next);
  };

  const applySettings = (s) => {
    const root = document.documentElement;
    // High contrast
    if (s.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    // Large text
    if (s.largeText) {
      root.style.fontSize = '120%';
    } else {
      root.style.fontSize = '';
    }
    // Dyslexia font
    if (s.dyslexiaFont) {
      root.classList.add('dyslexia-font');
    } else {
      root.classList.remove('dyslexia-font');
    }
  };

  useEffect(() => { applySettings(settings); }, []);

  return (
    <div className="relative" data-testid="accessibility-toggle">
      <Button
        variant="ghost"
        size="sm"
        className="h-9 w-9 p-0"
        onClick={() => setOpen(!open)}
        aria-label="Accessibility settings"
        data-testid="accessibility-btn"
      >
        <Eye size={18} />
      </Button>

      {open && (
        <div className="absolute right-0 top-11 w-64 bg-white border-2 border-gray-200 shadow-lg z-50 p-4 space-y-3" data-testid="accessibility-panel">
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">Accessibility</h4>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="flex items-center gap-2 text-sm">
              <Contrast size={16} /> High Contrast
            </span>
            <input
              type="checkbox"
              checked={!!settings.highContrast}
              onChange={e => update('highContrast', e.target.checked)}
              className="w-5 h-5 accent-[#002FA7]"
              data-testid="high-contrast-toggle"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="flex items-center gap-2 text-sm">
              <Type size={16} /> Larger Text
            </span>
            <input
              type="checkbox"
              checked={!!settings.largeText}
              onChange={e => update('largeText', e.target.checked)}
              className="w-5 h-5 accent-[#002FA7]"
              data-testid="large-text-toggle"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="flex items-center gap-2 text-sm">
              <Type size={16} /> Dyslexia-Friendly Font
            </span>
            <input
              type="checkbox"
              checked={!!settings.dyslexiaFont}
              onChange={e => update('dyslexiaFont', e.target.checked)}
              className="w-5 h-5 accent-[#002FA7]"
              data-testid="dyslexia-font-toggle"
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default AccessibilityToggle;
